import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStylesheets } from 'hooks/useStylesheets';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { getBootUserId } from 'hooks/useDashboardData';
import { useAppDispatch } from 'app/hooks';
import GlobalHeader from 'pages/dashboard/components/GlobalHeader';
import {
  partnersApi,
  useGetPartnersCompaniesQuery,
  useGetPartnersContactsQuery,
  useCreatePartnersCompanyMutation,
  usePatchPartnersCompanyMutation,
  useDeletePartnersCompanyMutation,
  useCreatePartnersContactMutation,
  usePatchPartnersContactMutation,
  useDeletePartnersContactMutation,
  type IPartnersCompany,
  type IPartnersContact,
} from 'app/services/partnersApi';
import CompanyRail from './components/CompanyRail';
import AddressForm from './components/AddressForm';
import ContactList from './components/ContactList';
import DeletePopup from './components/DeletePopup';
import PartnersFooter from './components/PartnersFooter';

/**
 * Partners CSS — the exact legacy cascade of `templates/partners/styles.html:1-4`,
 * in order, one `useStylesheets` call:
 *   header.css → partners/styles.css → common.css → bootstrap 3.3.4
 * `react.html` already ships common.css + header.css globally; re-injecting all
 * four here (appended after the globals) reproduces the last-occurrence order.
 *
 * ★ BOOTSTRAP IS REQUIRED and is deliberately loaded LAST, exactly as legacy
 * does: `styles.html:4` links it with a relative href that resolves to
 * `/static/vendor/...` from `/partners/`. The partners markup is pure
 * Bootstrap-3 grid (`col-xs-*`, `nav-tabs`, `tab-pane`, `dropdown-menu`) and
 * `partners/styles.css` defines none of it. Note `partners/styles.css` ALSO
 * `@import`s bootstrap at its own line 1 — that copy lands before the partners
 * rules (so partners wins over it), while this fourth `<link>` lands after
 * common.css. Both facts are legacy's; do not "de-duplicate" them.
 *
 * `css/partners/skeleton.css` exists on disk but legacy never loads it — NOT
 * added.
 */
const PARTNERS_CSS = [
  '/static/css/common/header.css',
  '/static/css/partners/styles.css',
  '/static/css/common/common.css',
  '/static/vendor/bootstrap-3.3.4-dist/css/bootstrap.min.css',
];

/** Loose bag of model attributes — what a Backbone `Model.set` call takes. */
type AttrBag = Record<string, unknown>;

/**
 * `Model.set`'s `changed` computation, reduced to what this page needs.
 *
 * Backbone's `set` walks the INCOMING attribute hash (`for (var attr in attrs)`)
 * and records `changed[attr] = val` for every key whose value differs from
 * `_previousAttributes` (`_.isEqual`). Every value on this page is a string, a
 * number or `null`, so `!==` is `_.isEqual`. Two consequences the write paths
 * lean on heavily:
 *
 *  1. ★ AN EMPTY `changed` MEANS NO `change` EVENT AND THEREFORE NO REQUEST.
 *     `set({city: null})` on a model whose `city` is already `null` fires
 *     nothing at all — which is exactly what clicking the ✕ on an
 *     already-empty field does (§4.4). Do not "helpfully" always PATCH.
 *  2. ★ THE KEY ORDER IS THE INCOMING HASH'S ORDER, and `this.changed` is what
 *     `save(this.changed, {patch:true})` serialises — so the PATCH body's key
 *     order is the SERVER RESPONSE's key order for the Q1 echo PATCHes.
 */
const backboneChanged = (prev: AttrBag, next: AttrBag): AttrBag => {
  const changed: AttrBag = {};
  Object.keys(next).forEach((key) => {
    if (prev[key] !== next[key]) changed[key] = next[key];
  });
  return changed;
};

/**
 * Re-resolve a matched row set against the CURRENT collection.
 *
 * Both search paths filter the collection and only THEN empty the DOM
 * (`searchCompanies` :266-275 → `companyRender` :316; `searchContactName`
 * :253-259 → `contactRender` :281), and emptying the DOM is what detaches a
 * focused-and-modified input and commits it (see `flushFocusedInput`). The
 * append loop that follows reads `search_row.get('name')` from the SAME model
 * objects — i.e. AFTER the commit. So legacy matches on the pre-commit values
 * and renders the post-commit ones, and this keeps that split honest now that
 * an RTK Query cache update produces new objects rather than mutating in place.
 */
const reresolve = <T extends { id: number }>(matched: T[], current: T[]): T[] =>
  matched.map((row) => current.find((candidate) => candidate.id === row.id) ?? row);

/**
 * `fn.uni_tools.accesptOnlyEnglish` (`static/mvc/universal_fn/universal_fn_tools
 * .js:998-1004`), the handler behind `keypress .press_english`
 * (`partners.js:45`, `:460-462`). Verbatim, including the deprecated `e.which`
 * (on a `keypress` that IS the char code) and the fact that the pattern permits
 * ONLY `[a-zA-Z0-9@._-]` — so a SPACE, a `!` and every accented or Cyrillic
 * letter are all blocked in the two email fields, not just non-Latin scripts.
 */
const accesptOnlyEnglish = (e: KeyboardEvent) => {
  const char = String.fromCharCode(e.which);
  const pattern = /[a-zA-Z0-9@._-]/;
  if (!pattern.test(char)) e.preventDefault();
};

/**
 * `fn.uni_tools.mobileKeyPress` (`universal_fn_tools.js:979-996`), behind
 * `keypress .phone-number-input` (`partners.js:47`, `:478-480`). Verbatim: it
 * matches on `e.key`, so letters AND `Enter` are blocked while `0-9-+(). ` pass.
 * The `controlKeys` list is legacy's; most of those keys never raise `keypress`
 * in Blink at all, which is why the filter looks stricter than it behaves.
 */
const mobileKeyPress = (e: KeyboardEvent) => {
  const allowedChars = '0123456789-+(). ';
  const controlKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
  if (allowedChars.includes(e.key) || controlKeys.includes(e.key)) return;
  e.preventDefault();
};

/**
 * `blockPaste` (`partners.js:464-476`), behind `paste .press_english` (`:46`).
 * A DIFFERENT and much wider allow-list than the keypress filter — the whole
 * printable-ASCII punctuation set plus `\s` — and it tests the ENTIRE pasted
 * string, so one Cyrillic character rejects the whole paste. `e.originalEvent`
 * is jQuery's unwrapping; a native listener already has the real event.
 */
const blockPaste = (e: ClipboardEvent) => {
  const clipboardData = e.clipboardData;
  if (!clipboardData) return;
  const pastedText = clipboardData.getData('text');
  const englishOnlyRegex = /^[A-Za-z0-9\s.,!?@#$%^&*()_\-+=:;"'<>/\\[\]{}|`~]*$/;
  if (!englishOnlyRegex.test(pastedText)) e.preventDefault();
};

/** One entry of the popup STACK — Q16 allows more than one at a time. */
interface PartnersPopup {
  key: number;
  title: string;
  content: string;
  name: string | null;
  onConfirm: () => void;
}

/**
 * Partners (`/partners/` — Backbone app `partners`), sub-phases 7a + 7b + 7c:
 * the data layer, the render, the two client-side searches, and now every
 * WRITE path plus the delete popups.
 *
 * A 1:1 port of `partners/static/partners/mvc/tmpl/partners.tmpl` (277 lines)
 * driven by the two collections `view/partners.js:59-61` fetches in parallel.
 *
 * ★★ 7c IN ONE SENTENCE: THERE IS NO SAVE BUTTON, NO DEBOUNCE AND NO DIRTY
 * STATE. Both models bind `this.listenTo(this,'change', this.onChange)` →
 * `this.save(this.changed, {patch:true})`
 * (`model/partners_all_company_unit.js:10-16`,
 * `model/partners_contact.js:16-22`), so **every `model.set()` is an immediate
 * PATCH of just the changed keys**. The DOM is already the user's typing and the
 * model follows it; nothing is ever "saved" as a unit. See `setCompanyAttrs` /
 * `setContactAttrs` — those two functions ARE the persistence layer.
 *
 * ★ THE HANDLERS ARE DELEGATED ON `#ksdm-content`, NOT BOUND PER ELEMENT.
 * `view/partners.js:25-48` is a Backbone `events` hash on `el: "#ksdm-content"`,
 * i.e. one jQuery delegated listener per (event, selector). 7c reproduces that
 * with THREE native listeners on the same node (`click`, `change`, `focusin`)
 * and a `closest()` dispatch, which is what jQuery's own delegation does. Three
 * reasons this is not a stylistic choice:
 *   1. `change` — NOT React's `onChange`. React maps `onChange` onto the native
 *      `input` event, which fires on every keystroke; legacy listens to the
 *      native `change`, which fires once on commit. Using `onChange` would turn
 *      one PATCH per edit into one PATCH per character.
 *   2. the rows added by `addContact` (§4.5) and by `contactRender` (Q8) are
 *      *different markup* from the template rows, and delegation covers all
 *      three shapes without threading handlers through four components.
 *   3. ★ it is what makes the DETACHED-INPUT COMMIT work — see
 *      `flushFocusedInput`.
 *
 * ★ ERRORS ARE SWALLOWED, DELIBERATELY (§4.7). Over-length input returns a
 * `500 text/html` error page (the serializer drops the model's `max_length`, so
 * Postgres is the only backstop) and the UI shows **nothing**: no error, no
 * revert, no retry. The DOM keeps the too-long value while the DB keeps the old
 * one, until a reload — or a reproduced `render()` — discards it. Every mutation
 * below therefore sends the request and ignores the outcome. This is a BACKEND
 * defect on the owner's list; mirroring it is the requirement.
 *
 * ★ THREE render paths, not one — `render()` (the Handlebars template),
 * `companyRender` (`partners.js:313-355`) and `contactRender` (`:277-312`) — and
 * the two search paths emit markup that has drifted from the template, so
 * searching mutates the page's shape and behaviour in ways a reload (or a
 * `render()`) is the only cure for. The individual quirks are documented at
 * their reproduction sites:
 *   Q3/Q4/Q10 in `components/CompanyRail.tsx`, Q8 in `components/ContactList
 *   .tsx`, the search row's six drifts in `components/ContactSearchRow.tsx`,
 *   Q2 in `components/AddedContactRow.tsx`, Q9/Q15/Q16 in
 *   `components/DeletePopup.tsx`, and Q1/Q5/Q6/Q7/Q11/Q13/Q14 plus the `.hide`
 *   toggle in this file.
 *
 * ★ BLANK UNTIL LOADED — DELIBERATE, DO NOT ADD A SPINNER.
 * `render()` runs only inside `Promise.all([companies.fetch(), contacts.fetch()])
 * .then(...)` (`partners.js:59-64`) and its `el` is `#ksdm-content`, which the
 * server ships EMPTY (`partners.html` never overrides `{% block content %}`).
 * The observed effect on dev3 is a white content pane for ~550 ms with header
 * and footer already painted, and no skeleton of any kind (discovery §1
 * "States"). The `loaded` gate below reproduces exactly that. It also
 * reproduces the ERROR behaviour: a rejected fetch means `.then` never runs, so
 * legacy stays blank forever — here `data` simply stays `undefined`. There is
 * no error UI anywhere on this page (§1) and none is added.
 *
 * ★ THE TWO GETs FIRE IN PARALLEL. Two RTK Query hooks in the same render
 * dispatch their thunks in the same commit, which is what `Promise.all` gives
 * legacy (observed: both at 509 ms, discovery §3).
 *
 * ★ SERVER ORDERING IS PRESERVED. Both endpoints are ordered by `name` via
 * `Meta.ordering` and legacy never re-sorts; neither do we, in either the rail,
 * the panes or the contact lists. The ONE exception is deliberate: a
 * just-created company is PINNED FIRST until the next reload (§4.1).
 *
 * ★ DUPLICATE IDs ARE INTENTIONAL — 97 of them (Q12). See the header comments
 * of `CompanyRail` (`company-tab`, the `<a id>`+`<input id>` pair) and
 * `ContactList`/`ContactRow` (`add_contact`, `contact_info_`) for why each one
 * is load-bearing.
 *
 * ★ NOT PORTED (proven dead — discovery §2, §8):
 *  - `click .done` → `doneBtn`: `.done` exists in NO partners template and is
 *    absent from the rendered DOM (count 0). The `active` class it would remove
 *    from `#partners` is therefore permanent, so it is hardcoded below.
 *  - `click .edit` → `editCompanyName`: `.edit` is 0 on load, and the only
 *    thing that ever emits it is the post-search phantom in `companyRender`
 *    (Q4), whose hardcoded `data-id="13"` makes it inert. 7b mirrored that
 *    item; it has since been dropped as a legacy bug at the owner's request,
 *    so the binding has nothing to bind to in either rail shape. See
 *    `CompanyRail`'s Q4 note — this is the port's one deliberate deviation.
 *  - `initialize`'s `a[name=partners_project_button]` active-state line (Q19),
 *    the `#contact_info` append loop (Q17) and `companyContactColl` (Q18) —
 *    all no-ops on every run.
 *  - `collection.remove(this.companyModel)` in both delete paths (Q20):
 *    `this.companyModel` is never assigned anywhere in the view, so both calls
 *    are `remove(undefined)`, a no-op. Likewise `deleteContact`'s
 *    `var a = self.models.filter(...)` (`:421-425`), assigned and discarded.
 *  - `changeInputContactFieldName`'s trailing `getCompanyContactCollection()`
 *    (`:171`): it re-derives `contacts` arrays onto the STALE
 *    `self.company_collection` snapshot, and the next `render()` rebuilds that
 *    snapshot from scratch first (`:91-92`) — so it can never be observed.
 *  - `controller/router.js`'s `#test` → `$('body').hide()` debug leftover, the
 *    same one the sizer port dropped.
 *
 * HEADER: `appName="partners"` reproduces `header.tmpl`'s `app_name` gating
 * (discovery §7 "What changes when the React header takes over") — NO shell
 * class (`partnersBase.html:13` has no class attribute), Dashboard icon not
 * active, `#partners_button` active (`header.tmpl:82`) and
 * `#workflow_popup_button` suppressed (`header.tmpl:37`). Pulled forward from
 * 7d by the orchestrator so the wave-A verify isn't dominated by header noise.
 */
const PartnersPage = () => {
  useDocumentTitle('Partners'); // partners.html `{% block title %} Partners {% endblock %}`

  // Gate first paint on the route CSS: these sheets are injected AFTER React
  // mounts, so without the gate the whole page paints as raw unstyled text in
  // the load gap (the FOUC class fixed on the dashboard, project and sizer
  // routes already). Legacy loads all four render-blocking in <head>.
  const cssLoaded = useStylesheets(PARTNERS_CSS);

  // `partnersBase.html:12` — `<body data-user_id="{{ user.id }}">`. `react.html`
  // has no such attribute; legacy header code reads it, so mirror the DOM.
  useEffect(() => {
    const id = getBootUserId();
    if (id != null) document.body.setAttribute('data-user_id', String(id));
    return () => {
      document.body.removeAttribute('data-user_id');
    };
  }, []);

  // ---- the page's only two requests (discovery §3) -------------------------
  const { data: companies } = useGetPartnersCompaniesQuery();
  const { data: contacts, refetch: refetchContacts } = useGetPartnersContactsQuery();
  const loaded = companies !== undefined && contacts !== undefined;

  // ---- the six mutations (discovery §3 endpoint table) ---------------------
  const dispatch = useAppDispatch();
  const [createCompanyReq] = useCreatePartnersCompanyMutation();
  const [patchCompanyReq] = usePatchPartnersCompanyMutation();
  const [deleteCompanyReq] = useDeletePartnersCompanyMutation();
  const [createContactReq] = useCreatePartnersContactMutation();
  const [patchContactReq] = usePatchPartnersContactMutation();
  const [deleteContactReq] = useDeletePartnersContactMutation();

  /**
   * ★ THESE TWO REFS ARE `self.companyCollection` / `self.contactCollection`.
   *
   * Legacy reads and writes the Backbone collections SYNCHRONOUSLY inside event
   * handlers and async success callbacks: `collection.get(id).set(...)` must see
   * the previous edit, `render()` must see a `destroy()` that happened
   * milliseconds earlier, and the `checkValidity()` revert reads
   * `model.get(name)` to decide what to put back in the DOM. An RTK Query cache
   * update is visible only on the NEXT render, so every write path mirrors it
   * into these refs at the same moment it dispatches, and every render re-syncs
   * them from the cache. The cache is still the single source of truth for
   * RENDERING — the refs exist purely so imperative code can read "the
   * collection as of right now".
   */
  /**
   * `dispatch(partnersApi.util.updateQueryData(...))`, with the one cast this
   * page needs. `partnersApi` reaches `ahuBoardApi` through `enhanceEndpoints`
   * (the only way a NEW tag type can be added to an existing `createApi`
   * instance), which widens the api's `TagTypes` union — so RTK types the
   * returned thunk against a `RootState` that is structurally wider than the one
   * the store's `AppDispatch` was derived from. The thunk itself is the ordinary
   * `updateQueryData` patch; nothing about it is special-cased. Every other
   * `injectEndpoints` call site in this repo dispatches these directly because
   * none of them adds a tag type.
   */
  const applyCacheUpdate = (thunk: unknown) => dispatch(thunk as never);

  const companiesRef = useRef<IPartnersCompany[] | undefined>(companies);
  companiesRef.current = companies;
  const contactsRef = useRef<IPartnersContact[] | undefined>(contacts);
  contactsRef.current = contacts;

  // =========================================================================
  // PERMISSIONS — which companies this user may actually write to
  // =========================================================================

  /**
   * ★ NOT A LEGACY BEHAVIOUR. Legacy offered every control on every row and let
   * the failures happen; this is a deliberate improvement, requested after a
   * tester watched `PATCH /projects/companies/133` come back
   * **404 "Company doesn't exist"** while the field sat there looking editable.
   *
   * The rule is ownership, and it is the API's rule, not one invented here:
   * `retrieve`/`update`/`partial_update` resolve the row as
   * `get(pk=pk, user=request.user)` (`api/rest_project/viewset.py:828`, `:847`,
   * `:863`), so a non-owner gets a 404 on every write — CMS admin or not, there
   * is no role that lifts it. `list` (`:802-816`) is the odd one out: a
   * `network` user is handed EVERY company, which is why the rail shows rows
   * whose fields the same user cannot save.
   *
   * Locked controls get `readonly` + `tabindex="-1"` + `non-clickable`, minus
   * that class's `opacity` — see `./readOnly`.
   *
   * `IPartnersCompany.user` is the owner id the serializer now returns (it did
   * not before — that field was added for this). `getBootUserId()` reads
   * `window.__KSDM_CONFIG__.user.id`, the same id Django rendered the shell
   * with, so the comparison is against the session's real user.
   *
   * ★ A NULL BOOT ID LOCKS EVERYTHING. It should never happen on an
   * authenticated page, and read-only is the safe direction to fail: an
   * over-locked pane is a support question, an under-locked one is a column of
   * controls that 404.
   *
   * ★ DELETE IS THE ONE ASYMMETRY, AND IT IS THE SERVER'S. `destroy` (`:877`)
   * does NOT filter by user, so the API would still honour a DELETE from a
   * non-owner; the kebab item is disabled anyway, because offering a
   * destructive action on somebody else's data while every other control on the
   * row is locked is worse than the inconsistency. Tightening the endpoint is a
   * separate call and deliberately not made here.
   */
  const bootUserId = getBootUserId();
  const editableIds = useMemo(() => {
    const ids = new Set<number>();
    if (bootUserId == null) return ids as ReadonlySet<number>;
    (companies ?? []).forEach((company) => {
      if (company.user === bootUserId) ids.add(company.id);
    });
    return ids as ReadonlySet<number>;
  }, [companies, bootUserId]);

  /** The delegated handlers below run outside render, so they read the set
   * through a ref for the same reason `companiesRef` exists. */
  const editableIdsRef = useRef<ReadonlySet<number>>(editableIds);
  editableIdsRef.current = editableIds;

  const isCompanyEditable = (id: number | null | undefined) =>
    id != null && editableIdsRef.current.has(id);

  /**
   * The company that owns an arbitrary node.
   *
   * Two cases, because the page has two places a company's controls live:
   *
   *  - the RAIL's `.change_name` sits in `ul#partnersTab`, outside every pane,
   *    and carries the company id in `data-id` — the same attribute
   *    `editCompanyNameText` reads to build its PATCH;
   *  - everything else (address fields, contact rows, their ✕ icons and kebabs)
   *    is inside the company's tab pane, `id="id-<companyId>"`. Resolved from
   *    the PANE and not from the nearest `data-id`, because a contact row's
   *    `data-id` is the CONTACT's id, and because a contact search (Q8)
   *    re-parents the rows up into `.contact-content` — the pane is the one
   *    ancestor that survives every shape this page can take.
   */
  const owningCompanyId = (node: Element | null): number | null => {
    if (node instanceof HTMLInputElement && node.classList.contains('change_name')) {
      const raw = node.dataset.id;
      const id = raw == null || raw === '' ? NaN : Number(raw);
      return Number.isFinite(id) ? id : null;
    }
    const pane = node?.closest('.tab-pane');
    const match = pane ? /^id-(\d+)$/.exec(pane.id) : null;
    return match ? Number(match[1]) : null;
  };

  /** Is the pane this node lives in writable by the current user? */
  const isNodeEditable = (node: Element | null) => isCompanyEditable(owningCompanyId(node));

  /**
   * `getCompanyContactCollection` (`partners.js:174-185`): for each company,
   * walk `contactCollection.models` in order and keep the ones whose
   * `company_id` loosely equals the company id. Grouping by key is the same
   * thing and keeps the server's name ordering intact.
   */
  const contactsByCompany = useMemo(() => {
    const grouped = new Map<number, IPartnersContact[]>();
    (contacts ?? []).forEach((contact) => {
      const key = Number(contact.company_id);
      const list = grouped.get(key);
      if (list) list.push(contact);
      else grouped.set(key, [contact]);
    });
    return grouped;
  }, [contacts]);

  // ---- tab state (Bootstrap's job in legacy — see CompanyRail) -------------
  /**
   * `render()` ends with `$('#partnersTab > li:nth-child(1) a.company-fields')
   * .click()` (`partners.js:99`), i.e. the FIRST company is active the instant
   * the markup exists, with no user action. Deriving the fallback rather than
   * setting it in an effect avoids a frame where no pane is active — legacy has
   * no such frame.
   *
   * ★ THREE-VALUED, and the third value matters. `undefined` = nothing has been
   * picked, so the derived active tab is the first company (the synthetic click
   * above). `null` = **no pane is active at all**, which is a state only
   * `companyRender` can produce: `partners.js:353` strips `active in` off the
   * visible pane and `:354`'s click then finds no `li` to re-activate when the
   * search matched nothing.
   */
  const [pickedId, setPickedId] = useState<number | null | undefined>(undefined);
  const activeId = pickedId === undefined ? companies?.[0]?.id ?? null : pickedId;

  /** Which anchors have ever been activated — Bootstrap only stamps
   * `aria-expanded` on tabs it has touched (bootstrap.js:2072-2083). */
  const [visited, setVisited] = useState<ReadonlySet<number>>(() => new Set());
  useEffect(() => {
    if (activeId == null) return;
    setVisited((prev) => (prev.has(activeId) ? prev : new Set(prev).add(activeId)));
  }, [activeId]);

  const handleActivate = useCallback((id: number) => setPickedId(id), []);

  // =========================================================================
  // 7b — the two searches (discovery §2 controls #9-#12, §5 Q3-Q11)
  // =========================================================================

  const companySearchRef = useRef<HTMLInputElement | null>(null);
  const contactSearchRef = useRef<HTMLInputElement | null>(null);

  /**
   * ★ Q6 — `self.activeCompanyId` (`partners.js:57`, written only by
   * `getActiveCompanyId` at `:114`).
   *
   * A STRING, because it is `e.target.id` verbatim, and its ONLY consumer is
   * contact search (`:252`, `:256`). Two things make it treacherous and both
   * are reproduced:
   *   1. it self-assigns — `render()` (`:99`) and `companyRender` (`:354`) both
   *      fire a SYNTHETIC click on the first company anchor, so it holds a
   *      value before the user has touched anything;
   *   2. it can capture garbage — clicking the `.company-tab` DIV rather than
   *      its `<a>`/`<input>` stores the literal `"company-tab"` (the div's
   *      duplicated id), after which contact search matches nothing and Q7
   *      fires. See `CompanyRail.handleCompanyTabClick`.
   * It is NOT the same thing as `activeId`: the visible tab and this variable
   * can and do diverge (Q11).
   */
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(null);

  /**
   * `companyRender`'s output (`partners.js:313-355`) — `null` until the first
   * company search, then the filtered list.
   *
   * ★ STICKY BY DESIGN (Q3/Q4/Q10). `companyRender` is the ONLY thing that
   * re-renders the rail after the first search, including when the search box
   * is cleared, so the rail keeps its second markup shape — and its dead delete
   * item — until a real page reload… OR until a `render()`, which rebuilds from
   * the Handlebars template. `doRender` is therefore the one place allowed to
   * reset this to `null`; nothing else may.
   */
  const [companyResults, setCompanyResults] = useState<IPartnersCompany[] | null>(null);

  /** Bumped on every `companyRender`. Used as the rail's `key`, because legacy
   * literally throws the `<li>`s away (`search_company.empty()`, `:316`) on
   * every keystroke — which is what resets its `.open`/`.top`/`readonly` state. */
  const [companyRenderSeq, setCompanyRenderSeq] = useState(0);

  /** `.adress-contact-name-header.hide` — `partners.js:351-352`. */
  const [headerHidden, setHeaderHidden] = useState(false);

  /** Per-company `contactRender` output (Q8) — see `ContactList`. */
  const [contactPanes, setContactPanes] = useState<Record<number, IPartnersContact[]>>({});

  // =========================================================================
  // 7c — write-path state
  // =========================================================================

  /**
   * ★ `render()`'s generation counter — the `key` of `<div id="partners">`.
   *
   * `render()` is `this.$el.html(this.template(data))` (`partners.js:98`): it
   * throws the ENTIRE `#ksdm-content` subtree away and rebuilds it from the two
   * collections. Bumping this key remounts the same subtree, which is the only
   * honest way to reproduce all six of its side effects at once —
   * see `doRender`. Only §4.1 (create company) and §4.9 (delete company) reach
   * it; every other write path leaves the DOM alone.
   */
  const [renderSeq, setRenderSeq] = useState(0);

  /** `render(addNewCompany = true)` (`:105-109`) — the id of the company that
   * was just created, consumed by `CompanyRail`'s two synthetic triggers and
   * cleared on the next commit. */
  const [newCompanyId, setNewCompanyId] = useState<number | null>(null);
  useEffect(() => {
    if (newCompanyId != null) setNewCompanyId(null);
  }, [newCompanyId]);

  /**
   * Contacts that entered the collection through `addContact` and have not been
   * through a `render()` yet — they render as `AddedContactRow` (Q2). Legacy has
   * no such flag because it never re-renders the row: the markup difference IS
   * the state. Cleared by `doRender`, at which point they become template rows,
   * exactly as a reload would.
   */
  const [appendedContactIds, setAppendedContactIds] = useState<ReadonlySet<number>>(
    () => new Set()
  );

  /**
   * Companies whose `<li>` has been removed by `deleteCompany`'s `elem.remove()`
   * (`:412`) but whose PANE is still in the DOM.
   *
   * ★ THIS IS NOT REDUNDANT WITH THE CACHE. Backbone's `destroy({wait:false})`
   * removes the model from the collection SYNCHRONOUSLY, before the DELETE is
   * even sent, and `elem.remove()` takes the `<li>` with it — but nothing
   * re-renders until the success callback's `render()`, so for the length of the
   * request the deleted company's tab pane is still on screen (still `active in`
   * if it was the visible one). Filtering only the RAIL here, and applying the
   * collection removal in `doRender`, reproduces that window instead of
   * collapsing both into one step.
   */
  const [deletedCompanyIds, setDeletedCompanyIds] = useState<ReadonlySet<number>>(
    () => new Set()
  );

  /** The popup STACK (Q16 — nothing stops a second one opening). */
  const [popups, setPopups] = useState<PartnersPopup[]>([]);
  const popupSeqRef = useRef(0);

  /** `#ksdm-content`, i.e. the Backbone view's `el` and the delegate root. */
  const contentRef = useRef<HTMLDivElement | null>(null);

  /**
   * The value each input had when it last received focus or last fired a
   * `change` — i.e. the browser's own "value at last change" bookkeeping, which
   * `flushFocusedInput` needs and which the DOM does not expose. A WeakMap so a
   * removed input takes its entry with it.
   */
  const lastCommittedRef = useRef(new WeakMap<HTMLInputElement, string>());

  /**
   * Latest-value handle for everything imperative. The delegated listeners are
   * bound ONCE (they must outlive every re-render, and `#ksdm-content` outlives
   * every `render()`), so they cannot close over render-scoped values; the
   * searches likewise need `flushFocusedInput` without taking a dependency on
   * it. Same pattern as `useBootstrapDropdown`'s `closeRef`.
   */
  const commitRef = useRef({
    flushFocusedInput: () => {},
    handleClick: (_event: MouseEvent) => {},
    handleChange: (_event: Event) => {},
    handleFocusIn: (_event: FocusEvent) => {},
  });

  /**
   * Q6 part 1 — the synthetic click at the end of `render()` (`partners.js:99`).
   * The ref latch keeps it to the FIRST render: a background refetch must not
   * silently move `activeCompanyId` back to the first company. With zero
   * companies the selector matches nothing and the variable stays `null`, which
   * is why the latch is only set on a non-empty list. Later `render()`s set it
   * explicitly in `doRender`.
   */
  const initialRenderClicked = useRef(false);
  useEffect(() => {
    if (initialRenderClicked.current) return;
    if (!companies || companies.length === 0) return;
    initialRenderClicked.current = true;
    setActiveCompanyId(String(companies[0].id));
  }, [companies]);

  /**
   * `searchCompanies` (`partners.js:263-276`) + `companyRender` (`:313-355`).
   *
   * ★ Q5 — THE COMPANY SEARCH IS CASE-BROKEN. The haystack is lowercased
   * (`model.get("name").toLowerCase()`, `:268-270`) but the needle is
   * `$(".search_company_name").val()` used RAW (`:271`). So `acco` finds
   * "ACCO Engineered Systems" and `ACCO` finds nothing — and, because an empty
   * result set hides the whole right-hand header strip, typing a capital letter
   * looks like the page broke. Reproduced exactly: `.toLowerCase()` on the
   * name, nothing on the needle.
   *
   * ★ The needle is read from the DOM, not from the event, so both bindings
   * (`keyup` :34 and the native `search` :35) behave identically. `indexOf('')`
   * is `0`, so clearing the box matches every company.
   *
   * ★ `new Backbone.Collection(...)` (`:265`) has no comparator — the survivors
   * keep the server's `name` ordering. No sort.
   */
  const runCompanySearch = useCallback(() => {
    if (!companies) return;
    const needle = companySearchRef.current?.value ?? '';
    const matched = (companiesRef.current ?? []).filter(
      (company) => company.name.toLowerCase().indexOf(needle) > -1
    );

    // ---- companyRender (:313-355) ----
    // :316 `search_company.empty()` — the line that detaches a focused,
    // modified `.change_name` and commits it. See `flushFocusedInput`.
    commitRef.current.flushFocusedInput();
    const found = reresolve(matched, companiesRef.current ?? []);

    setCompanyResults(found);
    setCompanyRenderSeq((seq) => seq + 1);
    // :351-352 — the ONLY place `.hide` is ever toggled. See the JSX below for
    // the asymmetry this creates for a genuinely empty account.
    setHeaderHidden(found.length === 0);

    // :353 — `$('.adress-contact-list > .row > .active').removeClass('active in')`
    // unconditionally deactivates the visible pane, and only :354's synthetic
    // click brings one back.
    const first = found.length ? found[0] : null;
    if (first) {
      setPickedId(first.id);
      // The rail's anchors are brand-new DOM, so Bootstrap's `aria-expanded`
      // history restarts from the one row :354 clicks.
      setVisited(new Set([first.id]));
      setActiveCompanyId(String(first.id)); // :354 → getActiveCompanyId
    } else {
      setPickedId(null);
      setVisited(new Set());
      // `activeCompanyId` is deliberately NOT cleared — no click happened.
    }
  }, [companies]);

  /**
   * `searchContactName` (`partners.js:248-262`) + `contactRender` (`:277-312`).
   *
   * ★ Q11 — IT IGNORES THE VISIBLE TAB. The filter is on `activeCompanyId` and
   * the render target is `.contact_<collection[0].company_id>`, never the pane
   * the user is looking at. After `companyRender` deactivates every pane
   * (`:353`) with no match to re-activate, a contact search still rewrites the
   * hidden pane of whatever company `activeCompanyId` last held.
   *
   * ★ Q7 ★ — IT THROWS, TWICE OVER:
   *   a) `collection[0].get('company_id')` (`:279`) is unguarded, so an empty
   *      result set is a `TypeError` (verified live: "Cannot read properties of
   *      undefined (reading 'get')");
   *   b) `model.get("name").toLowerCase()` (`:257`) is unguarded, so ANY
   *      contact with a `null` name — i.e. every freshly-added one — throws the
   *      same way, mid-`filter`.
   * Both are reproduced as REAL throws so the observable outcome is legacy's:
   * nothing renders and the pane is left exactly as it was. The `catch` is the
   * handler boundary and does nothing — legacy's exception escapes to
   * `window.onerror` and is likewise inert, but letting one escape here would
   * unmount React's tree, which is NOT parity.
   *
   * ★ The same case bug as Q5 lives here too: `:251` lowercases the needle into
   * a discarded temporary and `:257` then matches against `e.target.value` raw.
   */
  const runContactSearch = useCallback(
    (value: string) => {
      try {
        // :250-251 — legacy computes this and throws the result away.
        void value.toLowerCase();
        // :252 — with no active company (or the empty string) nothing happens
        // at all: no render, no clear, no error.
        if (!activeCompanyId) return;

        const models = (contactsRef.current ?? []).filter(
          (model) =>
            // :256 — `model.get("company_id") == self.activeCompanyId`, LOOSE.
            // The model holds a number and `activeCompanyId` a string, and JS
            // `number == string` is defined as `number === Number(string)` —
            // which is also why the garbage `"company-tab"` (→ NaN) can never
            // match anything.
            model.company_id === Number(activeCompanyId) &&
            // :257 — unguarded `.toLowerCase()` on a nullable column (Q7b). The
            // cast is what lets the TypeError happen instead of being typed
            // away; do not replace it with `?? ''`.
            (model.name as string).toLowerCase().includes(value)
        );

        // ---- contactRender (:277-312) ----
        // :279 — unguarded index 0 (Q7a). This line is the throw, and it comes
        // BEFORE the `.empty()` below, so a no-match never even detaches
        // anything.
        const contactDeleteRow = models[0].company_id;
        // :280-281 — `$('.contact_<id>').empty()` hits TWO elements and takes
        // the ADD button and the scroll container with it (Q8). `ContactList`
        // renders that surviving shape. As in `runCompanySearch`, emptying is
        // what commits a focused, modified `.contact_field`.
        commitRef.current.flushFocusedInput();
        setContactPanes((prev) => ({
          ...prev,
          [Number(contactDeleteRow)]: reresolve(models, contactsRef.current ?? []),
        }));
      } catch {
        // Q7 containment — see the doc comment. Legacy's error is confined to
        // the handler; so is this one.
      }
    },
    [activeCompanyId, contacts]
  );

  // =========================================================================
  // 7c — PERSISTENCE: `Model.set` → auto-PATCH (discovery §4 preamble)
  // =========================================================================

  /**
   * `self.companyCollection.get(id).set(attrs)` — and therefore, through the
   * model's own `change` binding, `PATCH /projects/companies/<id>` with just the
   * changed keys and NO trailing slash (Backbone `urlRoot` + `model.id`).
   *
   * ★ NO REQUEST WHEN NOTHING CHANGED (see `backboneChanged`). ★ NO REFETCH and
   * NO tag invalidation: legacy re-reads neither collection after a field edit,
   * so the cache is patched in place instead. ★ NO ERROR HANDLING: a 500 (§4.7)
   * or a 400 (`allow_blank=False` on an emptied `.change_name`) is swallowed,
   * leaving the DOM ahead of the database until the next `render()`.
   */
  const setCompanyAttrs = (id: number, attrs: AttrBag) => {
    const model = (companiesRef.current ?? []).find((company) => company.id === id);
    // Legacy would throw here (`collection.get(id)` → `undefined.set`). The only
    // way in is editing a field in the pane of a company deleted moments ago,
    // whose model `destroy()` already dropped from the collection — legacy sends
    // nothing in that case either, it just also logs an uncaught TypeError.
    if (!model) return;
    const changed = backboneChanged(model as unknown as AttrBag, attrs);
    if (Object.keys(changed).length === 0) return;
    companiesRef.current = (companiesRef.current ?? []).map((company) =>
      company.id === id ? ({ ...company, ...changed } as IPartnersCompany) : company
    );
    applyCacheUpdate(
      partnersApi.util.updateQueryData('getPartnersCompanies', undefined, (draft) => {
        const row = draft.find((company) => company.id === id);
        if (row) Object.assign(row, changed);
      })
    );
    void patchCompanyReq({ id, body: changed as Partial<IPartnersCompany> });
  };

  /** `self.contactCollection.get(id).set(attrs)` → `PATCH /projects/contacts/<id>`.
   * Same rules as `setCompanyAttrs`. */
  const setContactAttrs = (id: number, attrs: AttrBag) => {
    const model = (contactsRef.current ?? []).find((contact) => contact.id === id);
    if (!model) return;
    const changed = backboneChanged(model as unknown as AttrBag, attrs);
    if (Object.keys(changed).length === 0) return;
    contactsRef.current = (contactsRef.current ?? []).map((contact) =>
      contact.id === id ? ({ ...contact, ...changed } as IPartnersContact) : contact
    );
    applyCacheUpdate(
      partnersApi.util.updateQueryData('getPartnersContacts', undefined, (draft) => {
        const row = draft.find((contact) => contact.id === id);
        if (row) Object.assign(row, changed);
      })
    );
    void patchContactReq({ id, body: changed as Partial<IPartnersContact> });
  };

  /** `self.companyCollection.models = [...]` — a wholesale reassignment of the
   * collection, which only `addCompany` (§4.1, reorder-to-top) and
   * `deleteCompany` (§4.9, the `destroy()` removal) ever do. */
  const writeCompanies = (next: IPartnersCompany[]) => {
    companiesRef.current = next;
    applyCacheUpdate(
      partnersApi.util.updateQueryData('getPartnersCompanies', undefined, () => next)
    );
  };

  // =========================================================================
  // 7c — `render()`
  // =========================================================================

  /**
   * `render()` (`partners.js:89-111`), reproduced.
   *
   * `this.$el.html(this.template(data))` (`:98`) replaces every node under
   * `#ksdm-content`, so ALL of the following happens at once and all of it is
   * observable:
   *   1. every uncontrolled input is rebuilt from the COLLECTION — so a value
   *      that only ever existed in the DOM (§4.7's over-length string, which
   *      500'd) is silently discarded, and a value that was PATCHed is still
   *      there;
   *   2. ★ BOTH SEARCH BOXES ARE CLEARED — the template renders them with no
   *      value — and with them the two search RESULTS: `companyResults` goes
   *      back to `null` (so the rail returns to the TEMPLATE shape and
   *      `.company_delete` works again — the Q3 escape hatch), `contactPanes`
   *      is emptied (so Q8's destroyed panes get their ADD button and scroll
   *      container back) and `.adress-contact-name-header.hide` comes off;
   *   3. the rows `addContact` appended as raw HTML become ordinary template
   *      rows (`appendedContactIds` clears);
   *   4. the collection removal a `destroy()` performed lands in the cache, so
   *      the deleted company's PANE finally goes with its `<li>`;
   *   5. `.open`, `.top` and any cleared `readonly` are thrown away with the
   *      DOM, and Bootstrap's `aria-expanded` history restarts;
   *   6. any popup appended to `#ksdm-content` is destroyed with it — reachable
   *      by stacking a contact-delete popup under a company-delete one (Q16)
   *      and confirming the company.
   * Then `:99` fires a synthetic click on the first company's anchor, which
   * activates its tab AND sets `activeCompanyId` (Q6), and `:105-109` optionally
   * focuses and selects the new company's name.
   *
   * ★ NOT CALLED FROM ANYWHERE ELSE. `render()` has exactly three call sites in
   * legacy: the initial `Promise.all` (`:63`), `addCompany`'s success (`:194`)
   * and `deleteCompany`'s success (`:409`). A field edit, a contact add and a
   * contact delete all leave the DOM alone — do not add a fourth caller.
   */
  const doRender = (companiesAfter: IPartnersCompany[], addNewCompanyId?: number) => {
    // `:91-92` — the template data is read from the collection FIRST.
    writeCompanies(companiesAfter);
    setRenderSeq((seq) => seq + 1);
    setCompanyResults(null);
    setCompanyRenderSeq(0);
    setHeaderHidden(false);
    setContactPanes({});
    setAppendedContactIds(new Set());
    setDeletedCompanyIds(new Set());
    setPopups([]);
    // `:99` — the synthetic click on `li:nth-child(1) a.company-fields`.
    setPickedId(undefined);
    setVisited(new Set());
    // …which matches NOTHING on an empty rail, leaving `activeCompanyId` at its
    // previous value rather than clearing it.
    if (companiesAfter.length) setActiveCompanyId(String(companiesAfter[0].id));
    setNewCompanyId(addNewCompanyId ?? null);
  };

  // =========================================================================
  // 7c — the nine write paths (discovery §4)
  // =========================================================================

  /**
   * §4.3 — `change .address_field` → `changeInputName` (`partners.js:117-138`).
   *
   * ★ THE GUARD ORDER IS LOAD-BEARING AND IS LEGACY'S, EXACTLY (`:124-136`):
   *   1. `if (!value)` → `set({[name]: null})` — **with no validity check at
   *      all**. Clearing an `type=email` field that currently holds garbage
   *      therefore DOES persist, as `null`.
   *   2. else `if (!checkValidity())` → put the MODEL's value back into the DOM
   *      and **return without any request**. `input.value = null` is coerced to
   *      `""` by the IDL setter, which is why reverting an
   *      unset field visibly empties it.
   *   3. else `set({[name]: value})`.
   * ★ EMPTY IS ALWAYS `null`, NEVER `""` — the serializer's `allow_blank`
   * defaults to False, so `""` would 400 (`serializers.py:293-322`).
   * ★ The company id comes from `closest('.address-content').dataset.id`
   * (`:122-123`), not from a React prop, because that is the one lookup that
   * works identically for every row shape on the page.
   */
  const changeInputName = (elem: HTMLInputElement) => {
    const name = elem.name;
    const value = elem.value;
    const holder = elem.closest('.address-content') as HTMLElement | null;
    const id = Number(holder?.dataset.id);
    if (!value) {
      setCompanyAttrs(id, { [name]: null });
      return;
    }
    if (!elem.checkValidity()) {
      const model = (companiesRef.current ?? []).find((company) => company.id === id);
      const previous = model ? (model as unknown as AttrBag)[name] : undefined;
      elem.value = previous == null ? '' : String(previous);
      return;
    }
    setCompanyAttrs(id, { [name]: value });
  };

  /**
   * §4.6 — `change .contact_field` → `changeInputContactFieldName`
   * (`partners.js:148-172`). Identical guard order to §4.3, plus ONE extra line:
   *
   * ★ `localStorage['add:company:contact'] = <the typed value>` (`:165`) is
   * written on the NON-EMPTY, VALID branch ONLY, and BEFORE the `set()` — so it
   * fires even when the value did not actually change (and therefore no PATCH
   * goes out), and it does NOT fire when a field is cleared. That is the
   * cross-tab signal the Project Board reads on focus (§7); dropping it
   * silently stops its contact dropdown refreshing.
   *
   * ★ The contact id comes from `closest('.every-row').dataset.id` (`:153-154`)
   * — note that is the CONTACT's id, not the company's.
   */
  const changeInputContactFieldName = (elem: HTMLInputElement) => {
    const name = elem.name;
    const value = elem.value;
    const holder = elem.closest('.every-row') as HTMLElement | null;
    const id = Number(holder?.dataset.id);
    if (!value) {
      setContactAttrs(id, { [name]: null });
      return;
    }
    if (!elem.checkValidity()) {
      const model = (contactsRef.current ?? []).find((contact) => contact.id === id);
      const previous = model ? (model as unknown as AttrBag)[name] : undefined;
      elem.value = previous == null ? '' : String(previous);
      return;
    }
    localStorage.setItem('add:company:contact', value);
    setContactAttrs(id, { [name]: value });
  };

  /**
   * §4.2 — `change .change_name` → `editCompanyNameText`
   * (`partners.js:447-456`). Renaming a company inline.
   *
   * ★ NOTHING GATES THIS ONE. No `checkValidity()`, no empty→`null` coercion:
   * an emptied name really does `set({name: ""})`, really does
   * `PATCH {"name":""}`, and really does get a **400** ("This field may not be
   * blank") which nothing surfaces. Mirrored — do not add the guard the address
   * handler has.
   * ★ `localStorage['edit:company:name']` is the ID AS A STRING (`:454`,
   * `e.target.dataset.id`), and it is written UNCONDITIONALLY — including when
   * the name did not change and no PATCH went out.
   * ★ `readonly` goes back on via `setAttribute` (`:455`), an imperative write
   * `CompanyRail`'s `readOnly` prop is documented not to fight.
   * ★ NO RE-RENDER AND NO RE-SORT: the row keeps its position until a reload,
   * even though the collection is ordered by name.
   */
  const editCompanyNameText = (elem: HTMLInputElement) => {
    const editId = elem.dataset.id;
    const editName = elem.value;
    setCompanyAttrs(Number(editId), { name: editName });
    localStorage.setItem('edit:company:name', String(editId));
    elem.setAttribute('readonly', 'true');
  };

  /**
   * The three `change` handlers of the `events` hash, in BIND ORDER (`:32`,
   * `:33`, `:39`). jQuery would run every one whose selector matches the same
   * target, so the checks are independent `if`s rather than a switch — no input
   * on this page carries two of the three classes, but the shape is legacy's.
   */
  const commitFieldChange = (input: HTMLInputElement) => {
    // Permission gate. The `readOnly` attribute the components now render is the
    // affordance; this is the guarantee. An input can still fire `change` while
    // read-only — `flushFocusedInput` calls this directly, and so does
    // `clearTexField` — and a PATCH from here would 404 anyway.
    if (!isNodeEditable(input)) return;
    const classes = input.classList;
    if (classes.contains('address_field')) changeInputName(input);
    if (classes.contains('contact_field')) changeInputContactFieldName(input);
    if (classes.contains('change_name')) editCompanyNameText(input);
    lastCommittedRef.current.set(input, input.value);
  };

  /**
   * §4.4 — `click .text-clear` → `clearTexField` (`partners.js:140-146`).
   *
   * ```js
   * var input = $(e.target).prev();
   * input.val(''); input.text(''); input.trigger('change'); input.trigger('blur');
   * ```
   * ★ `$(e.target).prev()` is the PREVIOUS SIBLING ELEMENT, which is why the
   * `<input> → <i class="text-clear"> → <span class="placeholder-label">` triple
   * of §6.1 is load-bearing for behaviour as well as for CSS. Text nodes are
   * skipped by both `.prev()` and `previousElementSibling`.
   * ★ `input.text('')` is a no-op — jQuery 3.x assigns `textContent`, and an
   * `<input>` has none that renders. Not reproduced.
   * ★ `trigger('change')` runs jQuery's handlers directly (there is no
   * `elem.change()` method to call), i.e. it does NOT dispatch a native event.
   * Calling `commitFieldChange` is the same thing, and it is why an
   * ALREADY-EMPTY field still goes through the handler — where `backboneChanged`
   * finds nothing changed and sends NO request.
   * ★ `trigger('blur')` → jQuery's `special.blur.trigger` calls the native
   * `blur()` only when the element IS the active element; otherwise it just runs
   * (non-existent) jQuery blur handlers. Reproduced with that same condition.
   * ★ The same handler serves BOTH the address and the contact ✕ — the class is
   * on all 11 inputs' siblings — and dispatch happens purely on the input's own
   * classes.
   */
  const clearTexField = (target: HTMLElement) => {
    // Permission gate — the ✕ blanks the input BEFORE `commitFieldChange` runs,
    // so guarding only the commit would still wipe a locked field on screen.
    if (!isNodeEditable(target)) return;
    const input = target.previousElementSibling as HTMLInputElement | null;
    // jQuery on an empty set: `.val()`/`.trigger()` are silent no-ops.
    if (!input || input.tagName !== 'INPUT') return;
    input.value = '';
    commitFieldChange(input);
    if (document.activeElement === input) input.blur();
  };

  /**
   * ★★ THE DETACHED-INPUT COMMIT — an undocumented write path.
   *
   * When a focused input with a MODIFIED value is removed from the document,
   * the browser fires `change` on it (Blink drops focus in
   * `ContainerNode::RemoveChildren` BEFORE detaching, so the event still
   * bubbles). Legacy's `search_company.empty()` (`:316`) and
   * `search_contact.empty()` (`:281`) do exactly that, which means
   * **"type a new company name, then run a search" commits an immediate rename
   * PATCH plus `edit:company:name` with no blur and no user intent** — found by
   * the wave-B verifier, not by the discovery.
   *
   * This function makes that commit DETERMINISTIC instead of dependent on how a
   * given browser sequences focus loss against node removal: the two search
   * paths call it at exactly the line where legacy empties the container, and it
   * runs the same handler the native `change` would. If the browser ALSO fires
   * its own `change` during React's removal, the second pass finds nothing
   * changed in the model and sends no second request.
   *
   * ★ IT MUST NOT FIRE WHEN LEGACY WOULD NOT. Native `change` only fires when
   * the value differs from the value at the last focus/change — hence
   * `lastCommittedRef`. Without that test, every keystroke of a company search
   * would re-write `edit:company:name` while a name input happened to be
   * focused.
   *
   * ★ NOT called from `doRender`. There, legacy reads the collection into the
   * template data (`:91-92`) BEFORE `html()` detaches anything, so the rebuilt
   * DOM shows the PRE-commit value while the PATCH still goes out — which is
   * what happens here too if the browser fires the event. Forcing a flush first
   * would render the post-commit value and diverge. Both `render()` call sites
   * are reached from a real button click, which blurs and commits first anyway.
   */
  const flushFocusedInput = () => {
    const active = document.activeElement as HTMLInputElement | null;
    if (!active || active.tagName !== 'INPUT') return;
    const classes = active.classList;
    if (
      !classes.contains('address_field') &&
      !classes.contains('contact_field') &&
      !classes.contains('change_name')
    ) {
      return;
    }
    const at = lastCommittedRef.current.get(active);
    if (at === undefined || at === active.value) return;
    commitFieldChange(active);
  };

  /**
   * §4.1 — `click #add_company` → `addCompany` (`partners.js:186-200`).
   *
   * ```
   * → POST  /api/v1.0/projects/companies/      {"name":"Company Name"}
   * ← 201                                      {"id":<n>,"name":"Company Name", …9 nulls}
   * → PATCH /api/v1.0/projects/companies/<n>   {"id":<n>, …the 9 nulls}     ★ Q1
   * ```
   *
   * ★★ Q1 — THE ECHO PATCH. `collection.create()` posts the model, Backbone
   * `set()`s the server's response back onto it, that `set` finds ten changed
   * keys (`id`, plus the nine columns the POST body never mentioned and which
   * came back `null`) and the model's own `change` binding immediately PATCHes
   * them straight back. `name` is NOT in the body because it round-tripped
   * unchanged, and the server ignores `id` (`PrimaryKeyRelatedField(read_only)`).
   * It is observable on the wire and it is computed here, not hardcoded, by
   * running the real `changed` diff — the same function that produces every
   * other PATCH body.
   *
   * ★ THE NEW COMPANY IS PINNED FIRST, THEN SORTS ITSELF ON RELOAD. `:192-193`
   * reassigns `collection.models` to `[created, ...everything else]`; the server
   * orders by `name`, so after a reload it moves to its alphabetical slot.
   *
   * ★ `localStorage['add:company:name'] = <the Backbone Model>` → the string
   * `"[object Object]"` (Q13), written SYNCHRONOUSLY at `:199` — i.e. before the
   * POST has even resolved, and outside the success callback. Readers only test
   * truthiness (§7), so the garbage is reproduced verbatim as the lowest-risk
   * option.
   *
   * ★ NO REFETCH of either collection, and no error path: a failed POST means
   * the success callback never runs, so nothing at all happens (the localStorage
   * flag is still set).
   */
  const addCompany = async () => {
    const local: AttrBag = { name: 'Company Name' };
    const request = createCompanyReq({ name: 'Company Name' });
    // :199 — outside the success callback, so it lands before the 201.
    localStorage.setItem('add:company:name', '[object Object]');
    let created: IPartnersCompany;
    try {
      created = await request.unwrap();
    } catch {
      return;
    }
    // ★ Q1 — `user` is EXCLUDED from the diff. It is a field the serializer only
    // started returning for the permission gate; the echo PATCH must stay the
    // ten legacy keys, not eleven. (It is `read_only` server-side, so sending it
    // would be ignored rather than harmful — but the wire should still match.)
    const { user: _owner, ...echoSource } = created as unknown as AttrBag & { user: unknown };
    const echo = backboneChanged(local, echoSource);
    if (Object.keys(echo).length) {
      void patchCompanyReq({ id: created.id, body: echo as Partial<IPartnersCompany> });
    }
    // :191-195 — the success callback: reorder to the top, then render(true).
    const others = (companiesRef.current ?? []).filter((company) => company.id !== created.id);
    doRender([created, ...others], created.id);
  };

  /**
   * §4.5 — `click #add_contact` → `addContact` (`partners.js:202-246`).
   *
   * ```
   * → POST  /api/v1.0/projects/contacts/    {"name":null,"email":null,"tel_mobile":null,"company_id":"<n>"}
   * ← 201                                   {"id":<c>,…,"company_id":<n>}
   * → PATCH /api/v1.0/projects/contacts/<c> {"id":<c>,"company_id":<n>}     ★ Q1
   * ```
   *
   * ★ `company_id` GOES OUT AS A STRING and comes back as a NUMBER, because it
   * is read from `e.currentTarget.dataset.id` (`:204`). That string→number
   * difference is literally half of why the echo PATCH exists: the `changed`
   * diff sees `"103" !== 103`. The three `null`s come from the model's
   * `defaults` (`model/partners_contact.js:27-31`) and keep the echo body down
   * to two keys. Do not "clean up" either.
   *
   * ★ NO RE-RENDER. The success callback appends a RAW HTML STRING into
   * `.contact-content.contact_<id> .col-xs-12` (`:210-242`) — see
   * `AddedContactRow` for the three markup differences (Q2) that make input
   * filtering stop applying to a contact you just added.
   *
   * ★ NO localStorage WRITE AT ALL (Q14) — adding a contact and switching tabs
   * without typing leaves the Project Board's dropdown stale. Deliberate gap;
   * do not "fix" it.
   */
  const addContact = async (button: HTMLElement) => {
    // :204 — `self.company_id = e.currentTarget.dataset.id` (a STRING).
    const companyId = button.dataset.id ?? '';
    // Permission gate. The button also renders `disabled`, which stops the click
    // ever reaching the delegate; this covers a programmatic call.
    if (!isCompanyEditable(Number(companyId))) return;
    const local: AttrBag = {
      name: null,
      email: null,
      tel_mobile: null,
      company_id: companyId,
    };
    let created: IPartnersContact;
    try {
      created = await createContactReq({
        name: null,
        email: null,
        tel_mobile: null,
        company_id: companyId,
      }).unwrap();
    } catch {
      return;
    }
    // ★ Q1 — `{id, company_id}`.
    const echo = backboneChanged(local, created as unknown as AttrBag);
    if (Object.keys(echo).length) {
      void patchContactReq({ id: created.id, body: echo as Partial<IPartnersContact> });
    }
    // `collection.create` already put the model in the collection; the append
    // below is the DOM half, and it is NOT a re-render.
    contactsRef.current = [...(contactsRef.current ?? []), created];
    applyCacheUpdate(
      partnersApi.util.updateQueryData('getPartnersContacts', undefined, (draft) => {
        draft.push(created);
      })
    );
    setAppendedContactIds((prev) => new Set(prev).add(created.id));
  };

  /**
   * §4.9 — `deleteCompany` (`partners.js:399-416`), reached only through the
   * confirm popup.
   *
   * ```
   * → DELETE /api/v1.0/projects/companies/<id>   → 204
   * → GET    /api/v1.0/projects/contacts/        → 200      then render()
   * ```
   *
   * ★ COMPANIES ARE NEVER RE-FETCHED. The rail is rebuilt from the in-memory
   * collection, which `destroy({wait:false})` already emptied of this model —
   * that is why `render()` in the success callback shows the deletion even
   * though no `GET /companies/` ever happens. Contacts ARE re-fetched (their
   * rows cascade server-side: `ProjectCompanyContactPerson.company` is
   * `on_delete=CASCADE`, `models.py:63`).
   *
   * ★ `localStorage['delete:company:name'] = <the <li> DOM node>` → the string
   * `"[object HTMLLIElement]"` (Q13, `:405`). Written from the resolved node so
   * the coercion is the real one; on a miss legacy stores `"null"`, which is
   * still truthy and still triggers the Project Board's refresh.
   *
   * ★ ORDER: resolve `elem` FIRST (`:401`), then localStorage, then `destroy`,
   * then `elem.remove()`. `elem.remove()` here is the rail filter, NOT the cache
   * removal — see `deletedCompanyIds` for why the two are separate.
   */
  const deleteCompany = (companyDeleteId: string) => {
    const elem = document.querySelector(`li[data-id=item-${companyDeleteId}]`);
    // :404 — `collection.remove(this.companyModel)` is `remove(undefined)`, a
    // no-op (Q20). Not reproduced.
    localStorage.setItem('delete:company:name', String(elem));
    const id = Number(companyDeleteId);
    const model = (companiesRef.current ?? []).find((company) => company.id === id);
    if (!model) return; // legacy: `.get(id).destroy()` on undefined → TypeError
    setDeletedCompanyIds((prev) => new Set(prev).add(id));
    void deleteCompanyReq(id)
      .unwrap()
      .then(() => {
        // :407-410 — the success callback, in this order.
        void refetchContacts();
        doRender((companiesRef.current ?? []).filter((company) => company.id !== id));
      })
      .catch(() => {
        // §4.7 — nothing is shown on failure. The `<li>` stays removed, exactly
        // as legacy's already-executed `elem.remove()` leaves it.
      });
  };

  /**
   * §4.8 — `deleteContact` (`partners.js:417-432`), reached only through the
   * confirm popup.
   *
   * ```
   * → DELETE /api/v1.0/projects/contacts/<id>   → 204
   * → GET    /api/v1.0/projects/contacts/       → 200
   * ```
   * ★ NO company refetch, NO `render()`, and NO localStorage flag (Q14). The
   * only DOM change is the row disappearing.
   *
   * ★★ Q9 — AND THE OWNER'S ONE DELIBERATE DEVIATION.
   * The row is re-found by `div[data-name="company_<co>_<ct>"]` (`:419`), an
   * attribute `contactRender`'s searched rows DO NOT CARRY (`:283`). So deleting
   * a contact you found through a contact search resolves `elem` to `null`,
   * `elem.remove()` throws — AFTER `destroy()` has already sent the DELETE — and
   * because the throw escapes `DeletePopupView.confirmDelete` before its
   * `self.remove()` (`delete_popup.js:55-59`) the popup stays on screen too.
   * Net legacy outcome: contact gone server-side, row still visible, popup open.
   *
   * ★ REPRODUCED: the DELETE fires, the contacts refetch fires, the searched row
   * stays on screen (it renders from `contactPanes`, not from the collection),
   * and the popup stays open.
   * ★ DEVIATION (owner's explicit decision, discovery §11.3): Escape and Cancel
   * keep working, so the user is not trapped in a modal that only a reload can
   * clear. Nothing else about the outcome is softened — in particular the row is
   * NOT removed and `data-name` is NOT added back to the searched rows.
   */
  const deleteContact = (contactDeleteId: string, companyContactDeleteName: string | undefined) => {
    // :419 — an undefined `data-name` interpolates to the LITERAL string
    // "undefined", which matches nothing. That is Q9's mechanism; keep it.
    const elem = document.querySelector(`div[data-name="${companyContactDeleteName}"]`);
    // :420 — `contactCollection.remove(self.companyModel)` → no-op (Q20).
    // :421-425 — `var a = self.models.filter(...)`, assigned and discarded.
    const id = Number(contactDeleteId);
    const model = (contactsRef.current ?? []).find((contact) => contact.id === id);
    if (!model) return; // legacy: `.get(id).destroy()` on undefined → TypeError
    // `destroy({wait:false})` drops the model from the collection synchronously,
    // BEFORE the request. For a template row that is also what removes it from
    // the DOM, i.e. `elem.remove()`; for a searched row it changes nothing on
    // screen, which is the whole of Q9.
    contactsRef.current = (contactsRef.current ?? []).filter((contact) => contact.id !== id);
    applyCacheUpdate(
      partnersApi.util.updateQueryData('getPartnersContacts', undefined, (draft) =>
        draft.filter((contact) => contact.id !== id)
      )
    );
    void deleteContactReq(id)
      .unwrap()
      .then(() => {
        // :427-429 — `contactCollection.fetch({reset:true})`. No render().
        void refetchContacts();
      })
      .catch(() => {});
    // :431 — `elem.remove()` on `null`. The throw is what leaves the popup open.
    if (!elem) {
      throw new TypeError("Cannot read properties of null (reading 'remove')");
    }
  };

  /**
   * §4.10 — `openCompanyDeletePopup` (`partners.js:377-397`).
   *
   * ★ `event.target.dataset.id`, NOT `currentTarget`. The menu item is
   * `<a class="remove company_delete" data-id="<n>"><i class="icon-trash"></i>Delete</a>`,
   * so a click that lands on the trash GLYPH has `target` = the `<i>`, which
   * carries no `data-id` → `collection.get(undefined)` → `undefined` →
   * `model.get("name")` THROWS and NO POPUP OPENS. Clicking the word "Delete"
   * works. Reproduced (the `if (!model) return` below is that throw's observable
   * effect) — and note the early return also skips `stopPropagation`, so the
   * click reaches Bootstrap's `clearMenus` and the kebab menu closes, exactly as
   * it does in legacy.
   *
   * ★ `event.stopPropagation()` (`:395`) is the LAST line. It keeps the opening
   * click from reaching the document, so Bootstrap's `clearMenus` never runs and
   * the kebab menu stays OPEN behind the popup.
   */
  const openCompanyDeletePopup = (event: MouseEvent, target: HTMLElement) => {
    const companyDeleteId = target.dataset?.id;
    const model = (companiesRef.current ?? []).find(
      (company) => String(company.id) === String(companyDeleteId)
    );
    if (!model) return;
    // Permission gate. The menu item is rendered `.disabled` and WITHOUT the
    // `company_delete` class for a company this user does not own, so the
    // delegate never routes here; this is the belt to that pair of braces.
    if (!isCompanyEditable(model.id)) return;
    popupSeqRef.current += 1;
    const key = popupSeqRef.current;
    const id = String(companyDeleteId);
    setPopups((prev) => [
      ...prev,
      {
        key,
        title: 'Delete Partner?',
        content:
          'If you choose to Delete, Partner Company and all its Contacts will be lost and will not be recoverable.',
        // `model.get("name")` verbatim — a NULL name renders an empty `<li>`.
        name: model.name,
        onConfirm: () => deleteCompany(id),
      },
    ]);
    event.stopPropagation();
  };

  /**
   * §4.10 — `openContactDeletePopup` (`partners.js:356-376`).
   *
   * ★ It captures BOTH `event.target.dataset.name` and `.dataset.id` (`:358-359`)
   * at OPEN time, and `data-name` is what `deleteContact` later uses to find the
   * row — so a searched row (no `data-name`) is already doomed here, before the
   * user has confirmed anything. Q9.
   * ★ `'Empty contact'` FALLBACK (`:365`): a contact with no name yet — i.e.
   * every freshly-added one — is listed as "Empty contact" instead of a blank
   * line. The company popup has no such fallback.
   * ★ Same `event.target` glyph trap as the company popup.
   */
  const openContactDeletePopup = (event: MouseEvent, target: HTMLElement) => {
    // Permission gate — a contact belongs to its company, so a locked pane
    // locks its contact rows too.
    if (!isNodeEditable(target)) return;
    const companyContactDeleteName = target.dataset?.name;
    const contactDeleteId = target.dataset?.id;
    const model = (contactsRef.current ?? []).find(
      (contact) => String(contact.id) === String(contactDeleteId)
    );
    if (!model) return;
    popupSeqRef.current += 1;
    const key = popupSeqRef.current;
    const id = String(contactDeleteId);
    setPopups((prev) => [
      ...prev,
      {
        key,
        title: 'Delete Contact?',
        content: 'If you choose to Delete, Contact will be lost and will not be recoverable.',
        name: model.name ? model.name : 'Empty contact',
        onConfirm: () => deleteContact(id, companyContactDeleteName),
      },
    ]);
    event.stopPropagation();
  };

  // =========================================================================
  // 7c — the delegate root (`events` hash on `el: "#ksdm-content"`)
  // =========================================================================

  /**
   * jQuery's delegation, reproduced: walk from `event.target` up to the delegate
   * root testing each selector, run every handler that matches, and hand the
   * handler the MATCHED element as `currentTarget` while `target` stays the node
   * that was really clicked. `closest()` is that same walk.
   *
   * The five click bindings that write, in `events`-hash order (`:27-31`). The
   * other four (`.done`, `.edit`, `.company-tab`, `.company-tab a`) are dead or
   * read-only and live in `CompanyRail`.
   */
  const handleDelegatedClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    // `click .text-clear` (:27). `clearTexField` reads `e.target`, and
    // `.text-clear` is a childless `<i>`, so matched === target.
    if (target.closest('.text-clear')) clearTexField(target);

    // `click #add_company` (:28).
    if (target.closest('#add_company')) void addCompany();

    // `click #add_contact` (:29) — `e.currentTarget.dataset.id`, i.e. the
    // BUTTON, not the `<i>`/text inside it. The id is duplicated 42 times, so
    // this has to be the matched element and not a document lookup.
    const addContactBtn = target.closest('#add_contact');
    if (addContactBtn) void addContact(addContactBtn as HTMLElement);

    // `click .company_delete` (:30) — ★ Q3: the search-shaped rail emits
    // `class="remove"` WITHOUT this class, so after any company search this
    // never matches and company deletion is dead for the session.
    if (target.closest('.company_delete')) openCompanyDeletePopup(event, target);

    // `click .contact_delete` (:31) — present on all three row shapes.
    if (target.closest('.contact_delete')) openContactDeletePopup(event, target);
  };

  /** `change .address_field` / `.contact_field` / `.change_name` (`:32`, `:33`,
   * `:39`). The two search inputs also fire `change`; none of the three classes
   * matches them, so nothing happens — as in legacy. */
  const handleDelegatedChange = (event: Event) => {
    const input = event.target as HTMLInputElement | null;
    if (!input || input.tagName !== 'INPUT') return;
    commitFieldChange(input);
  };

  /** Not a legacy binding: the bookkeeping `flushFocusedInput` needs. `focusin`
   * (which bubbles, unlike `focus`) records the value an input starts an editing
   * session with, which is what the browser compares against to decide whether
   * `change` should fire at all. */
  const handleDelegatedFocusIn = (event: FocusEvent) => {
    const input = event.target as HTMLInputElement | null;
    if (!input || input.tagName !== 'INPUT') return;
    lastCommittedRef.current.set(input, input.value);
  };

  commitRef.current = {
    flushFocusedInput,
    handleClick: handleDelegatedClick,
    handleChange: handleDelegatedChange,
    handleFocusIn: handleDelegatedFocusIn,
  };

  /**
   * `keypress .press_english` (`partners.js:45`), `paste .press_english` (`:46`)
   * and `keypress .phone-number-input` (`:47`) — the three INPUT FILTERS, which
   * are live on 69 inputs each: address `email` + contact `email` carry
   * `press_english`, address `tel_office` + contact `tel_mobile` carry
   * `phone-number-input`.
   *
   * ★ THEY ARE DELEGATED, AND THAT IS WHY Q2 WORKS. The binding is on
   * `#ksdm-content` against a CLASS, so it covers every row shape that has the
   * class — and covers NONE of the rows that lack it. `addContact`'s raw string
   * (`AddedContactRow`) and `contactRender`'s (`ContactSearchRow`) both drop
   * both classes, so a just-added or just-searched contact accepts letters in
   * Mobile and Cyrillic in Email until the next `render()` promotes it to a
   * template row. Do not attach these per-input: that would silently "fix" Q2.
   *
   * ★ These are the reason a phone field can never hold a letter on this page,
   * so they change what reaches the wire, not just what the user sees: legacy
   * PATCHes `{"tel_office":"(818) 555-1212 "}` for typed
   * `(818) 555-1212 ext`, and an email that the filter has stripped down to a
   * VALID address PATCHes, where the unfiltered string would have failed
   * `checkValidity()` and reverted instead.
   */
  const handleDelegatedKeyPress = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.closest('.press_english')) accesptOnlyEnglish(event);
    if (target.closest('.phone-number-input')) mobileKeyPress(event);
  };

  const handleDelegatedPaste = (event: ClipboardEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.closest('.press_english')) blockPaste(event);
  };

  /**
   * The delegated listeners, bound ONCE to `#ksdm-content` — the node Backbone
   * binds to, and the one node `render()` does not replace. Bound natively
   * rather than through React props for the three reasons in this file's header
   * comment; the three stateful ones dispatch through `commitRef` so they never
   * need re-binding and can never go stale, while the two filters are pure
   * functions of their event and are bound directly.
   */
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return undefined;
    const onClick = (event: MouseEvent) => commitRef.current.handleClick(event);
    const onChange = (event: Event) => commitRef.current.handleChange(event);
    const onFocusIn = (event: FocusEvent) => commitRef.current.handleFocusIn(event);
    root.addEventListener('click', onClick);
    root.addEventListener('change', onChange);
    root.addEventListener('focusin', onFocusIn);
    root.addEventListener('keypress', handleDelegatedKeyPress);
    root.addEventListener('paste', handleDelegatedPaste);
    return () => {
      root.removeEventListener('click', onClick);
      root.removeEventListener('change', onChange);
      root.removeEventListener('focusin', onFocusIn);
      root.removeEventListener('keypress', handleDelegatedKeyPress);
      root.removeEventListener('paste', handleDelegatedPaste);
    };
  }, [cssLoaded]);

  /**
   * `"search .search_company_name"` (`partners.js:35`) and
   * `"search .search_contact_name"` (`:37`) — the NATIVE `search` event an
   * `<input type="search">` fires when the browser's own ✕ is clicked or Enter
   * is pressed. React exposes no `onSearch` prop, so it is bound imperatively.
   * The `keyup` twins (`:34`, `:36`) are plain `onKeyUp` props below.
   *
   * `renderSeq` is a dependency because a reproduced `render()` remounts both
   * inputs, and the listeners have to follow the new nodes.
   */
  useEffect(() => {
    const companyEl = companySearchRef.current;
    const contactEl = contactSearchRef.current;
    const onCompanySearch = () => runCompanySearch();
    const onContactSearch = (e: Event) => runContactSearch((e.target as HTMLInputElement).value);
    companyEl?.addEventListener('search', onCompanySearch);
    contactEl?.addEventListener('search', onContactSearch);
    return () => {
      companyEl?.removeEventListener('search', onCompanySearch);
      contactEl?.removeEventListener('search', onContactSearch);
    };
  }, [loaded, renderSeq, runCompanySearch, runContactSearch]);

  /**
   * The rail's rows. `companyResults` (a search) wins over the collection, and
   * `deletedCompanyIds` then takes out the `<li>` a `deleteCompany` has already
   * removed while its pane is still on screen.
   */
  const railCompanies = useMemo(() => {
    const base = companyResults ?? companies ?? [];
    if (deletedCompanyIds.size === 0) return base;
    return base.filter((company) => !deletedCompanyIds.has(company.id));
  }, [companyResults, companies, deletedCompanyIds]);

  // Hold a blank frame until the route stylesheets have applied.
  if (!cssLoaded) return null;

  return (
    <>
      <GlobalHeader appName="partners" />

      {/* ★ HEIGHT-CHAIN REPAIR — `100vh`, NOT `100%`. Measured on live dev3
          (`getComputedStyle`): legacy's `#ksdm-content` is **807px** at a 900px
          viewport, i.e. `partners/styles.css:11-13`'s `height: calc(100% - 93px)`
          RESOLVES — `common.css` sets `html { height:100% }` and
          `partners/styles.css:1-4` sets `body { height:100% }`, so `100%` is the
          viewport. The window does NOT scroll on legacy (`body.scrollHeight ===
          clientHeight`); the company rail (`ul#partnersTab`, `overflow:auto`,
          643px) and `.scrollable-area` (537px) scroll internally instead.

          `react.html` inserts `<div id="react-root">` between `body` and this
          node, and nothing gives it a height — so a literal `calc(100% - 93px)`
          here resolves against an INDEFINITE parent and computes to **0px**,
          collapsing the whole chain: the rail stops scrolling and grows to
          1554px, `.scrollable-area` shrinks 537→52px, the detail card is cut
          off mid-form, the window scrolls (1761px) and the footer is thrown to
          `x=1600, w=0`. `100vh` is the same number legacy computes (900 − 93 =
          807) without depending on the parent chain.

          The inline style is still required to out-rank the dashboard CSS leak
          `:global(body #ksdm-content){height:calc(100vh - 53px)}`
          (`pages/dashboard/components/StatusesRail.module.scss:22`), which
          applies on EVERY React route and would otherwise make this 847px. */}
      <div id="ksdm-content" ref={contentRef} style={{ height: 'calc(100vh - 93px)' }}>
        {loaded && (
          /* `this.$el.find("#partners").addClass("active")` (`partners.js:104`)
             runs on every render and only `.done` — which does not exist —
             would ever remove it, so `active` is part of the markup here.

             ★ `key={renderSeq}` IS THE `render()` REPRODUCTION. Legacy's
             `this.$el.html(...)` replaces this entire subtree; remounting it is
             what rebuilds every uncontrolled input from the collection, clears
             both search boxes and drops every piece of DOM-only state. See
             `doRender` for the full list. */
          <div key={renderSeq} id="partners" className="partners col-xs-12 active">
            <div className="content-item full-height">
              <div className="partners-heading col-xs-12">
                <span>Partners</span>
              </div>
              <div className="tab-content col-xs-12">
                {/* ---- header strip (tmpl:7-57) ---- */}
                <div className="row">
                  <div className="col-xs-3 company-name-header">
                    <div className="row">
                      <div className="col-xs-12">
                        <div className="col-xs-1">
                          <span className="icon-business-24px" />
                        </div>
                        <div className="col-xs-7">
                          <span>Company</span>
                        </div>
                        <div className="col-xs-4 add-button-block">
                          {/* `click #add_company` → `addCompany` (§4.1) is
                              DELEGATED on `#ksdm-content` (`partners.js:28`),
                              hence no `onClick` here. No `type` attribute in the
                              template either — kept absent so the rendered
                              attribute set matches. */}
                          <button
                            id="add_company"
                            className="ka-has-tooltip btn-add btn add"
                            tooltip="Add new partner company"
                          >
                            <i style={{ fontSize: '17px', paddingRight: '6px' }}>+</i> ADD
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* `companyRender` toggles `.hide` on this node when a company
                      search matches nothing (`partners.js:351-352`) and takes
                      it off again on any match. `.hide` is Bootstrap's
                      `display:none !important`, so the whole right-hand strip —
                      "Address", "Contact" and the CONTACT SEARCH BOX itself —
                      vanishes on a no-match.
                      ★ ASYMMETRY PRESERVED: an account with genuinely zero
                      companies is NOT hidden, because the toggle lives only in
                      `companyRender` and `render()` never calls it. Hence
                      `headerHidden` starts `false`, is only ever written by
                      `runCompanySearch`, and is reset by `doRender`. */}
                  <div
                    className={`col-xs-9 adress-contact-name-header${
                      headerHidden ? ' hide' : ''
                    }`}
                  >
                    <div className="row">
                      <div className="col-xs-5 adress-name-header">
                        <div className="">
                          <div className="col-xs-12">
                            <div className="col-xs-3 col-icon-block">
                              <span>Address</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-xs-7 contact-name-header">
                        <div className="row">
                          <div className="col-xs-9">
                            <div className="row">
                              <div className="col-xs-12">
                                <div className="col-xs-2">
                                  <span>Contact</span>
                                </div>
                                <div className="contact-name-search col-xs-9">
                                  <div className="row">
                                    <div className="col-xs-12 search-block">
                                      {/* `keyup`/`search .search_contact_name`
                                          → `searchContactName` (§2 #11-#12,
                                          Q7/Q8/Q11). No debounce in legacy —
                                          every keystroke re-filters and
                                          re-renders — and none added here. */}
                                      <input
                                        ref={contactSearchRef}
                                        type="search"
                                        placeholder="Search"
                                        className="search_contact_name"
                                        onKeyUp={(e) => runContactSearch(e.currentTarget.value)}
                                      />
                                      <i className="icon-search-1" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ---- company search strip (tmpl:58-86) ---- */}
                <div className="row row-search-block">
                  <div className="company-name-search col-xs-3">
                    <div className="row">
                      <div className="col-xs-12">
                        <div className="col-xs-12 search-block">
                          {/* `keyup`/`search .search_company_name` →
                              `searchCompanies` (§2 #9-#10, Q3/Q4/Q5/Q10). No
                              debounce in legacy; none added here. */}
                          <input
                            ref={companySearchRef}
                            type="search"
                            placeholder="Search"
                            className="search_company_name"
                            onKeyUp={runCompanySearch}
                          />
                          <i className="icon-search-1" />
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* The template's commented-out duplicate contact search
                      block (tmpl:70-85) is dead markup — not ported. */}
                </div>

                {/* ---- master/detail (tmpl:87-273) ---- */}
                <div className="row partners-content">
                  {/* ★ The rail is the ONLY thing a company search re-renders —
                      the panes below are untouched by `companyRender`, which is
                      why a filtered rail still sits next to all 42 panes and
                      why a no-match leaves every pane deactivated rather than
                      removed.
                      ★ The changing `key` remounts the rail on every keystroke,
                      exactly as `search_company.empty()` (`partners.js:316`)
                      does: `.open`, `.top` and any `readonly` a user had
                      cleared are all thrown away with the DOM. */}
                  <CompanyRail
                    key={companyResults ? `search-${companyRenderSeq}` : 'template'}
                    companies={railCompanies}
                    variant={companyResults ? 'search' : 'template'}
                    activeId={activeId}
                    visited={visited}
                    onActivate={handleActivate}
                    onCompanyTabClick={setActiveCompanyId}
                    newCompanyId={newCompanyId}
                    editableIds={editableIds}
                  />
                  <div className="col-xs-9 full-height">
                    <div className="full-height">
                      <div className="col-xs-12 adress-contact-list">
                        <div className="row full-height">
                          {/* ★ NOT filtered by `deletedCompanyIds`: legacy's
                              `elem.remove()` takes the `<li>` only, so a
                              deleted company's pane stays in the DOM (and stays
                              `active in` if it was visible) until the success
                              callback's `render()` rebuilds from the
                              collection. */}
                          {companies.map((company) => {
                            /* Bootstrap's pane leg: `.fade` + `active` + `in`
                               (bootstrap.js:2080-2088 — `transition` is true
                               here because the container's children ARE
                               `.fade`). */
                            const isActive = company.id === activeId;
                            return (
                              <div
                                key={company.id}
                                className={`col-xs-12 tab-pane fade${
                                  isActive ? ' active in' : ''
                                }`}
                                id={`id-${company.id}`}
                                role="tabpanel"
                                /* `?? ''` — `name` is nullable, and Handlebars
                                   interpolates a null as the empty string, so
                                   legacy emits `aria-labelledby="-tab"`. A bare
                                   template literal would emit the literal
                                   `"null-tab"` (verified against live dev3). */
                                aria-labelledby={`${company.name ?? ''}-tab`}
                              >
                                <div className="contact-address-content input-label-group row">
                                  {/* Everything in this pane is read-only for a
                                      company the current user does not own — see
                                      `editableIds`. */}
                                  <AddressForm
                                    company={company}
                                    readOnly={!editableIds.has(company.id)}
                                  />
                                  <ContactList
                                    companyId={company.id}
                                    readOnly={!editableIds.has(company.id)}
                                    contacts={contactsByCompany.get(company.id) ?? []}
                                    /* Q8 — non-null once `contactRender` has
                                       emptied this pane. Keyed by company id,
                                       NOT by visibility: the destroyed shape
                                       survives tab switches and company
                                       searches, because nothing short of a
                                       reload or a `render()` rebuilds the
                                       panes. */
                                    searchRows={contactPanes[company.id] ?? null}
                                    /* Q2 — rows added since the last `render()`
                                       carry `addContact`'s raw-string markup,
                                       not the template's. */
                                    appendedIds={appendedContactIds}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ★ THE POPUPS LIVE HERE, NOT IN A PORTAL AND NOT INSIDE `#partners`.
            `this.$el.append(popupView.$el)` (`partners.js:373`, `:393`) appends
            them to `#ksdm-content` as its LAST CHILDREN — so they survive a
            search but NOT a `render()`, which is why `doRender` empties the
            stack. More than one at a time is legal (Q16). */}
        {popups.map((popup) => (
          <DeletePopup
            key={popup.key}
            title={popup.title}
            content={popup.content}
            name={popup.name}
            onConfirm={popup.onConfirm}
            onClose={() =>
              setPopups((prev) => prev.filter((candidate) => candidate.key !== popup.key))
            }
          />
        ))}
      </div>

      <PartnersFooter />
    </>
  );
};

export default PartnersPage;
